import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CTextarea,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { createItem, updateItem, ItemRequestStatus } from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import DocumentError, { newDocumentError } from "../../models/document-error";

interface DocumentErrorFormProps {
  initialDocumentError?: DocumentError;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const DocumentErrorForm: React.FC<DocumentErrorFormProps> = ({
  initialDocumentError,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [editingDocumentError, setEditingDocumentError] =
    useState<DocumentError>(
      initialDocumentError ? initialDocumentError : newDocumentError()
    );

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocumentError({
      ...editingDocumentError,
      errorCode: e.target.value,
    });
  };

  const onRawMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingDocumentError({
      ...editingDocumentError,
      rawMessage: e.target.value || undefined,
    });
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingDocumentError({
      ...editingDocumentError,
      description: e.target.value,
    });
  };

  const onPossibleSolutionsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setEditingDocumentError({
      ...editingDocumentError,
      possibleSolutions: e.target.value || undefined,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendDocumentError = {
      ...editingDocumentError,
    };
    let requestPromise: Promise<ItemRequestStatus<DocumentError>>;
    if (editingDocumentError.id === undefined) {
      requestPromise = createItem<DocumentError>(
        "/document_errors/create/",
        toSendDocumentError
      );
    } else {
      requestPromise = updateItem<DocumentError>(
        `/document_errors/${toSendDocumentError.id}/`,
        toSendDocumentError
      );
    }

    const documentErrorStatus = await requestPromise;

    if (documentErrorStatus.status !== SUCCESS) {
      if (documentErrorStatus.errors !== undefined) {
        setErrors(documentErrorStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (documentErrorStatus.detail !== undefined) {
        message = documentErrorStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingDocumentError(newDocumentError());
  };

  useEffect(() => {
    setEditingDocumentError(
      initialDocumentError ? initialDocumentError : newDocumentError()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDocumentError]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Código de error:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingDocumentError.errorCode)}
                onChange={onCodeChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Mensaje original:</CLabel>
            </CCol>
            <CCol>
              <CTextarea
                rows={2}
                value={emptyValueOnUndefined(editingDocumentError.rawMessage)}
                onChange={onRawMessageChange}
                placeholder="Mensaje exacto devuelto por el sistema"
              ></CTextarea>
              <FieldErrors
                errors={getFieldErrors("rawMessage", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Descripción: *</CLabel>
            </CCol>
            <CCol>
              <CTextarea
                rows={3}
                value={emptyValueOnUndefined(editingDocumentError.description)}
                onChange={onDescriptionChange}
                placeholder="Descripción comprensible del error"
              ></CTextarea>
              <FieldErrors
                errors={getFieldErrors("description", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Posibles soluciones:</CLabel>
            </CCol>
            <CCol>
              <CTextarea
                rows={3}
                value={emptyValueOnUndefined(
                  editingDocumentError.possibleSolutions
                )}
                onChange={onPossibleSolutionsChange}
                placeholder="Posibles causas o soluciones sugeridas"
              ></CTextarea>
              <FieldErrors
                errors={getFieldErrors("possibleSolutions", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default DocumentErrorForm;
